import { FC, ReactNode } from 'react';
import { IconButton, Modal, Box, Typography, useMediaQuery, Theme } from '@mui/material';
import { CloseIcon, EventBackground, Showable, useStyles } from '@sweatpals/react-ui-kit';
import {
	CreateEventTicketDto,
	FileVariantsEnum,
	formatEventTimeRange,
	DEFAULT_CURRENCY
} from '@/../../libs/shared/src';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';
import Logo from '@/modules/_core/components/logo/logo.component';
import { EventType } from '@/modules/_core/types/event-types';
import Image from 'next/image';
import { isValid } from 'date-fns';
import PriceTierCard from '../price-tier-card/price-tier-card.component';
import { useQueryEventReservedSpots } from '@/modules/_core/hooks/events/use-query-event-reserved-spots.hook';
import { selectTicketModalStyles } from './select-ticket-modal.styles';

export interface SelectTicketModalProps {
	open: boolean;
	ticketsQuantity: number;
	joinWaitlistButtonText: ReactNode;
	buyButton: ReactNode;
	eventId?: string;
	selectedShortLocalInstance?: string;
	eventData?: EventType;
	instance?: string;
	tickets?: CreateEventTicketDto[];
	isPurchaseAllowedDueToSalesStartTime?: boolean;
	clearTickets: () => void;
	addTicket: (eventPriceId: string) => void;
	removeTicket: (eventPriceId: string) => void;
	onClose: () => void;
	openJoinWaitlistModal: (eventPriceId: string) => void;
}

const SelectTicketModal: FC<SelectTicketModalProps> = ({
	open,
	eventId,
	instance,
	onClose,
	eventData,
	isPurchaseAllowedDueToSalesStartTime,
	addTicket,
	removeTicket,
	clearTickets,
	ticketsQuantity,
	tickets,
	joinWaitlistButtonText,
	buyButton,
	openJoinWaitlistModal,
}) => {
	const currency = eventData?.author?.stripeConnectedAccountCurrency || DEFAULT_CURRENCY;
	const smMedia = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

	const styles = useStyles(selectTicketModalStyles);

	const { publishedPrices, freeSpotsCount, eventTicketsCountAndSpots, userTicketReservations } =
		useQueryEventReservedSpots(
			{
				eventId,
				instance,
				eventData,
			},
			10000
		);

	const isWaitlistEnabled = !!eventData?.isWaitlistEnabled;

	const background =
		eventData?.background &&
		getPublicFileUrl(eventData?.background, {
			variant: FileVariantsEnum.XS,
		});

	return (
		<Modal open={open} onClose={onClose} component="div">
			<Box sx={styles.modalContainer}>
				<EventBackground background={background} />

				<Box sx={styles.modalNav}>
					<Box sx={styles.navContainer}>
						<Logo type="light" sx={styles.logo} layout="fill" />
						<IconButton sx={styles.closeButton} onClick={onClose}>
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>

				<Box sx={styles.contentWrapper}>
					<Box sx={styles.eventInfoBox}>
						{background && (
							<Image
								src={background}
								style={{ borderRadius: '4px', objectFit: 'cover' }}
								alt="Event Image"
								width={70}
								height={70}
								fetchPriority="auto"
							/>
						)}
						<Box sx={styles.eventInfoContent}>
							<Typography sx={styles.eventName}>{eventData?.name}</Typography>
							<Typography sx={styles.eventDate}>
								<Showable show={isValid(new Date(eventData?.startDate)) && !eventData?.additionalData?.hideEventDate}>
									{eventData?.startDate
										? formatEventTimeRange(eventData?.startDate, eventData?.endDate, {
												startDateFormat: 'EEE, MMM d, h:mm a',
												endDateFormat: 'h:mm a',
												separator: ' - ',
												showTimezone: false,
											})
										: ''}
								</Showable>
							</Typography>
						</Box>
					</Box>
					<Box sx={styles.ticketsContainer}>
						<Box sx={styles.tiersContainer}>
							<Typography sx={styles.tierLabel}>Select ticket tier:</Typography>

							{publishedPrices?.map((tier, index) => (
								<PriceTierCard
									currency={currency}
									key={tier.id}
									index={index}
									priceTier={tier}
									isSmallSize={!smMedia}
									freeSpotsCount={freeSpotsCount - ticketsQuantity}
									onlySingleAttendanceAllowed={eventData?.onlySingleAttendanceAllowed}
									isPurchaseAllowedDueToSalesStartTime={isPurchaseAllowedDueToSalesStartTime}
									eventTicketsCountAndSpots={eventTicketsCountAndSpots}
									userTicketReservations={userTicketReservations}
									joinWaitlistButtonText={joinWaitlistButtonText}
									quantity={tickets?.find((t) => t.eventPriceId === tier.id)?.quantity}
									// TODO change to `price.isWaitlistEnabled` when implemented
									hideWaitlist={!isWaitlistEnabled}
									onJoinWaitlist={() => openJoinWaitlistModal(tier.basePriceId)}
									onAdd={() => addTicket(tier.id)}
									onRemove={() => removeTicket(tier.id)}
									onSubscribe={() => {
										clearTickets();
										addTicket(tier.id);
									}}
								/>
							))}
						</Box>
						<Showable show={smMedia}>{buyButton}</Showable>
					</Box>
				</Box>
				<Showable hide={smMedia}>
					<Box sx={styles.bottomButtonContainer}>{buyButton}</Box>
				</Showable>
			</Box>
		</Modal>
	);
};

export default SelectTicketModal;
